import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Target, GraduationCap, BarChart3, Zap, Calendar, Users, MessageCircle, Search, TrendingUp } from "lucide-react";
import ProgressJourney from "../../../components/ProgressJourney";
import { COLORS, BRAND_PURPLE, HEADING_COLOR } from "../../../constants/colors";
import { upskillingPathwayService } from "../../../services/upskillingPathwayService";

const ExistingUserWithPathwayHome = () => {
  const navigate = useNavigate();
  const { firstName } = useSelector((state) => state.auth);
  const { targetJob } = useSelector((state) => state.user);
  
  // State for dynamic pathway counts
  const [pathwayCounts, setPathwayCounts] = useState({
    courses: 0,
    skills: 0,
    tools: 0,
    certificates: 0,
    lastUpdated: null
  });
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Fetch upskilling pathway counts on component mount
  useEffect(() => {
    const fetchPathwayCounts = async () => {
      try {
        setIsLoading(true);
        setHasError(false);
        const response = await upskillingPathwayService.getUpskillingPathwayCounts();
        
        if (response.success) {
          setPathwayCounts(response.data);
        } else {
          console.warn("No upskilling pathway found for user");
          setHasError(true);
        }
      } catch (error) {
        console.error("Error fetching pathway counts:", error);
        // If it's a 404 error (no pathway found), don't treat it as an error
        if (error.response?.status === 404) {
          console.warn("No upskilling pathway found for user");
          setHasError(false);
        } else {
          setHasError(true);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchPathwayCounts();
  }, []);

  const pathwayCards = [
    {
      icon: <Target className="w-6 h-6 text-white"/>,
      title: "TARGET CAREER",
      value: targetJob || "AI Product Manager",
      description: "Your personalized pathway starts here.",
      progress: 100,
      progressText: "Complete"
    },
    {
      icon: <GraduationCap className="w-6 h-6 text-white" />,
      title: "RECOMMENDED COURSES",
      value: isLoading ? "Loading..." : hasError ? "Create Pathway" : `${pathwayCounts.courses} Courses`,
      description: "Tailored to your pathway",
      progress: isLoading ? 0 : hasError ? 0 : Math.min((pathwayCounts.courses / 5) * 100, 100),
      progressText: isLoading ? "Loading..." : hasError ? "No pathway found" : `${Math.floor(pathwayCounts.courses * 0.6)} of ${pathwayCounts.courses} completed`
    },
    {
      icon: <BarChart3 className="w-6 h-6 text-white"/>,
      title: "SKILLS REQUIRED",
      value: isLoading ? "Loading..." : hasError ? "Create Pathway" : `${pathwayCounts.skills} Skills`,
      description: "Key competencies needed",
      progress: isLoading ? 0 : hasError ? 0 : Math.min((pathwayCounts.skills / 10) * 100, 100),
      progressText: isLoading ? "Loading..." : hasError ? "No pathway found" : `${Math.floor(pathwayCounts.skills * 0.67)} of ${pathwayCounts.skills} acquired`
    },
    {
      icon: <Zap className="w-6 h-6 text-white"/>,
      title: "RECOMMENDED TOOLS",
      value: isLoading ? "Loading..." : hasError ? "Create Pathway" : `${pathwayCounts.tools} AI Tools`,
      description: "ChatGPT, Tableau + more",
      progress: isLoading ? 0 : hasError ? 0 : Math.min((pathwayCounts.tools / 8) * 100, 100),
      progressText: isLoading ? "Loading..." : hasError ? "No pathway found" : `${Math.floor(pathwayCounts.tools * 0.5)} of ${pathwayCounts.tools} mastered`
    }
  ];

  const accelerateItems = [
    { icon: <Users className="w-5 h-5" />, text: "Connect with a Mentor", path: "/dashboard/mentors-and-community/mentorship", enabled: true },
    { icon: <Users className="w-5 h-5" />, text: "Find a Learning Buddy", path: "/dashboard/learning-buddy", enabled: false },
    { icon: <MessageCircle className="w-5 h-5" />, text: "Connect with your Communities", path: "/dashboard/mentors-and-community/community", enabled: true },
    { icon: <Search className="w-5 h-5" />, text: "Search Jobs", path: "/dashboard/job-support/jobs", enabled: true },
    { icon: <Users className="w-5 h-5" />, text: "Join the AI Talent Pool", path: "/dashboard/job-support/ai-talent-pool", enabled: true },
    { icon: <TrendingUp className="w-5 h-5" />, text: "Start a Business", path: "/dashboard/business", enabled: false }
  ];

  return (
    <div className="bg-white min-h-screen py-4 sm:py-6 md:py-8 px-3 sm:px-4 md:px-6">
      <div className="max-w-7xl mx-auto space-y-6 sm:space-y-8">
        <div className="text-center">
          <h1 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-3 px-2" style={{ color: HEADING_COLOR }}>
            Hello {firstName || "Alex"}, Let's Keep accelerating towards your AI Proof Career
          </h1>
          <p className="text-gray-500 text-xs sm:text-sm max-w-4xl mx-auto px-2">
            You are making great progress, Connect with a Mentor, a Learning Buddy and your Communities
          </p>
        </div>

        <ProgressJourney currentStep={6} totalSteps={6} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white border border-gray-300 rounded-xl shadow-sm overflow-hidden">
              <div className="p-3 sm:p-4 mb-3 sm:mb-4" style={{ backgroundColor: COLORS.purpleLight }}>
                <div className="flex gap-2 sm:gap-3">
                  <div className="w-7 h-7 sm:w-9 sm:h-9 mt-1 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: BRAND_PURPLE }}>
                    <Target className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">Your Upskilling Pathway</h2>
                    <p className="text-gray-500 text-xs sm:text-sm">Your personalized journey based on your career decision and assessments.</p>
                  </div>
                </div>
              </div>
              <div className="p-3 sm:p-4 md:p-5 pt-0 mb-3 sm:mb-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  {pathwayCards.map((card, index) => (
                    <div key={index} className="bg-white border border-gray-300 rounded-xl p-3 sm:p-4 shadow-sm">
                      <div className="text-center">
                        <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3" style={{ backgroundColor: BRAND_PURPLE }}>
                          {React.cloneElement(card.icon, { className: "w-3 h-3 sm:w-4 sm:h-4 text-white" })}
                        </div>
                        
                        <h3 className="text-[9px] sm:text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">{card.title}</h3>
                        <p className="text-xs sm:text-sm font-bold mb-1" style={{ color: BRAND_PURPLE }}>{card.value}</p>
                        <p className="text-xs text-gray-500 mb-2 sm:mb-3 leading-tight">{card.description}</p>
                        
                        <div className="w-full bg-gray-200 rounded-full h-1 sm:h-1.5 mb-2">
                          <div 
                            className="h-1 sm:h-1.5 rounded-full transition-all duration-300" 
                            style={{ width: `${card.progress}%`, backgroundColor: BRAND_PURPLE }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-700">{card.progressText}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="px-3 sm:px-4 md:px-5 pb-3 sm:pb-4 md:pb-5">
                <button
                  onClick={() => navigate("/dashboard/skills-and-learning/my-upskilling-pathway")}
                  className="w-full text-black border border-gray-400 py-2.5 sm:py-3 px-3 sm:px-4 rounded-xl hover:from-pink-600 hover:to-purple-700 transition-colors font-medium flex items-center justify-center gap-2 sm:gap-3 text-sm"
                >
                  <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
                  View the Full Upskilling Plan
                </button>
              </div>
            </div>
          </div>
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-300 rounded-xl p-4 sm:p-6 shadow-sm">
              <div className="flex gap-2 sm:gap-3 mb-3 sm:mb-4">
                <Zap className="w-6 h-6 sm:w-7 sm:h-7 mt-1 sm:mt-2 flex-shrink-0" style={{ color: BRAND_PURPLE }} />
                <div className="min-w-0">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900">Accelerate your Journey</h3>
                  <p className="text-gray-500 text-xs sm:text-sm">Get support and connect with your community</p>
                </div>
              </div>
              
              <div className="space-y-2 sm:space-y-4">
                {accelerateItems.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => item.enabled && navigate(item.path)}
                    disabled={!item.enabled}
                    className={`w-full py-2 sm:py-2.5 px-3 rounded-lg font-medium flex items-center gap-2 sm:gap-3 text-xs sm:text-sm transition-colors ${
                      item.enabled 
                        ? "text-black border border-gray-400 hover:bg-gray-100" 
                        : "text-gray-400 border border-gray-200 bg-gray-50 cursor-not-allowed"
                    }`}
                  >
                    {React.cloneElement(item.icon, { 
                      className: `w-4 h-4 sm:w-5 sm:h-5 ${!item.enabled ? "text-gray-400" : ""}` 
                    })}
                    <span className="leading-tight">{item.text}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExistingUserWithPathwayHome; 